"use client";

import type { MutationOptions } from "@tanstack/react-query";
import { useMutation } from "@tanstack/react-query";
import gql from "graphql-tag";
import type {
  CanvasSchedulerResponse,
  CreateCanvasTriageAppointmentInput,
} from "@rmh/defs";
import type { GeneratedMutation } from "@aws-amplify/api-graphql";
import { fetchGraphQLClient } from "../../../amplify/fetchGraphQLClient";

interface CreateCanvasTriageAppointmentVariables {
  input: CreateCanvasTriageAppointmentInput;
}

interface CreateCanvasTriageAppointmentResponse {
  data?: {
    createCanvasTriageAppointment: CanvasSchedulerResponse;
  };
}

export const createCanvasTriageAppointmentMutation = gql`
  mutation createCanvasTriageAppointment(
    $input: CreateCanvasTriageAppointmentInput!
  ) {
    createCanvasTriageAppointment(input: $input) {
      success
      error {
        type
        message
        details
      }
      data {
        appointmentId
        canvasAppointmentId
        canvasPractitionerId
        appointmentDateTime
        timestamp
      }
    }
  }
`.loc?.source.body as GeneratedMutation<
  CreateCanvasTriageAppointmentVariables,
  {
    createCanvasTriageAppointment: CanvasSchedulerResponse;
  }
>;

export const useCreateCanvasTriageAppointment = (
  options: MutationOptions<
    CreateCanvasTriageAppointmentResponse,
    Error,
    CreateCanvasTriageAppointmentVariables
  > = {}
) => {
  const { mutateAsync, isPending } = useMutation<
    CreateCanvasTriageAppointmentResponse,
    Error,
    CreateCanvasTriageAppointmentVariables
  >({
    ...options,
    mutationFn: async (variables) =>
      fetchGraphQLClient(createCanvasTriageAppointmentMutation, variables),
  });

  const createCanvasTriageAppointment = async (
    input: CreateCanvasTriageAppointmentInput
  ) => {
    try {
      const response = await mutateAsync({ input });

      const payload = response?.data?.createCanvasTriageAppointment;

      return payload;
    } catch (error) {
      console.error("Failed to create Canvas triage appointment", error);
      throw error;
    }
  };

  return {
    createCanvasTriageAppointment,
    isPending,
  };
};
